import {
  Button,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Switch,
  Text
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { BankSelect } from '../../../components/BankSelect'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { MaskedInput, PIX_KEY_MASKS } from '../../../components/MaskedInput'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { Tooltip } from '../../../components/Tooltip'
import { PixKeyType, useAppToast } from '../../../helpers'
import { CommercialConsultantService } from '../../../services/CommercialConsultant/CommercialConsultantService'
import { SelectCommercialConsultant } from '../../../services/CommercialConsultant/types'
import { EventService } from '../../../services/Event/EventService'
import { SelectEvent } from '../../../services/Event/types'
import EventCouponService from '../../../services/EventCoupons/EventCouponService'
import {
  CouponCommissionType,
  FreeCouponCategory,
  IEventCoupon,
  IEventCouponCreatePayload
} from '../../../services/EventCoupons/interfaces'
import { TeacherService } from '../../../services/Teacher/TeacherService'
import { SelectTeacher } from '../../../services/Teacher/types'
import {
  commissionTypeOptions,
  discountTypeOptions,
  freeCouponCategoryOptions,
  pixKeyTypeOptions
} from './couponLabels'

interface IProps {
  isOpen: boolean
  onClose: () => void
  coupon?: IEventCoupon | null
  mode: 'create' | 'edit'
  onSuccess: () => void
}

// Um desconto integral é o tipo "Ingresso grátis": só ele pula a cobrança e entra
// no relatório de cortesias. O backend recusa 100% com a mesma mensagem.
const MAX_PERCENTAGE_DISCOUNT = 99.99

// O cupom global não é "nada selecionado": ele precisa de um valor próprio no
// SearchableSelect, que reserva '' para o placeholder.
const GLOBAL_EVENT_VALUE = 'global'

const baseForm: IEventCouponCreatePayload = {
  event_id: null,
  ticket_id: null,
  code: '',
  discount_type: 'percentage',
  discount_value: null,
  max_uses: 1,
  teacher_id: null,
  commission_type: null,
  commercial_consultant_id: null,
  partner_name: null,
  partner_bank_code: null,
  partner_bank_name: null,
  partner_bank_agency: null,
  partner_bank_account: null,
  partner_bank_holder_name: null,
  partner_pix_key: null,
  partner_pix_key_type: null,
  partner_phone: null,
  commission_rate: null,
  commission_rate_sold_out: null,
  free_coupon_category: null,
  expires_at: null,
  is_active: true
}

export function EventCouponFormModal({ isOpen, onClose, coupon, mode, onSuccess }: IProps) {
  const toast = useAppToast()
  const service = useMemo(() => new EventCouponService(), [])
  const eventService = useMemo(() => new EventService(), [])
  const teacherService = useMemo(() => new TeacherService(), [])
  const consultantService = useMemo(() => new CommercialConsultantService(), [])
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<IEventCouponCreatePayload>(baseForm)
  const [events, setEvents] = useState<SelectEvent[]>([])
  const [teachers, setTeachers] = useState<SelectTeacher[]>([])
  const [consultants, setConsultants] = useState<SelectCommercialConsultant[]>([])

  const isGlobal = form.event_id === null
  const selectedEvent = events.find((e) => e.id === form.event_id)
  const tickets = selectedEvent?.tickets ?? []
  const showDiscountValue = form.discount_type === 'percentage' || form.discount_type === 'fixed'
  const isFreeTicket = form.discount_type === 'free_ticket'
  const isCommissioned = form.commission_type !== null
  const isUnlimited = form.max_uses === null
  const pixKeyMask = form.partner_pix_key_type ? PIX_KEY_MASKS[form.partner_pix_key_type] : null
  // Um cupom só serve para evento que ainda vai acontecer, então os encerrados
  // ficam fora da lista — exceto o do próprio cupom em edição, que precisa
  // continuar aparecendo mesmo depois do evento passar.
  const eventOptions = useMemo(() => {
    const now = Date.now()
    const hasNotEnded = (ev: SelectEvent) => ev.ends_at === null || new Date(ev.ends_at).getTime() >= now
    return [
      { value: GLOBAL_EVENT_VALUE, label: '🌐 Todos os eventos (cupom global)' },
      ...events
        .filter((ev) => hasNotEnded(ev) || ev.id === form.event_id)
        .map((ev) => ({ value: String(ev.id), label: ev.name }))
    ]
  }, [events, form.event_id])
  const teacherOptions = useMemo(() => teachers.map((t) => ({ value: String(t.id), label: t.name })), [teachers])
  const consultantOptions = useMemo(
    () => consultants.map((c) => ({ value: String(c.id), label: c.name })),
    [consultants]
  )

  useEffect(() => {
    eventService
      .listForSelect()
      .then(setEvents)
      .catch(() => toast.error('Erro ao carregar eventos'))
  }, [eventService, toast])

  useEffect(() => {
    // Includes inactive teachers: a coupon can stay linked to a professor who has
    // since left, and it must still be possible to select them here.
    teacherService
      .listForSelectIncludingDisabled()
      .then(setTeachers)
      .catch(() => toast.error('Erro ao carregar professores'))
  }, [teacherService, toast])

  useEffect(() => {
    consultantService
      .listForSelect()
      .then(setConsultants)
      .catch(() => toast.error('Erro ao carregar consultores'))
  }, [consultantService, toast])

  useEffect(() => {
    if (!isOpen) return
    if (mode === 'edit' && coupon) {
      setForm({
        event_id: coupon.event_id,
        ticket_id: coupon.ticket_id,
        code: coupon.code,
        discount_type: coupon.discount_type,
        discount_value: coupon.discount_value,
        max_uses: coupon.max_uses,
        teacher_id: coupon.teacher_id,
        commission_type: coupon.commission_type,
        commercial_consultant_id: coupon.commercial_consultant_id,
        partner_name: coupon.partner_name,
        partner_bank_code: coupon.partner_bank_code,
        partner_bank_name: coupon.partner_bank_name,
        partner_bank_agency: coupon.partner_bank_agency,
        partner_bank_account: coupon.partner_bank_account,
        partner_bank_holder_name: coupon.partner_bank_holder_name,
        partner_pix_key: coupon.partner_pix_key,
        partner_pix_key_type: coupon.partner_pix_key_type,
        partner_phone: coupon.partner_phone,
        commission_rate: coupon.commission_rate,
        commission_rate_sold_out: coupon.commission_rate_sold_out,
        free_coupon_category: coupon.free_coupon_category,
        expires_at: coupon.expires_at ? coupon.expires_at.slice(0, 10) : null,
        is_active: coupon.is_active
      })
    } else {
      setForm(baseForm)
    }
  }, [isOpen, mode, coupon])

  function generateCode() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const prefix = Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
    const suffix = String(Math.floor(Math.random() * 10000)).padStart(4, '0')
    setForm((f) => ({ ...f, code: prefix + suffix }))
  }

  function handleEventChange(value: string) {
    // The global option means a coupon valid for every event — it cannot
    // target an event-specific ticket, so ticket_id is reset to null.
    const eventId = value === GLOBAL_EVENT_VALUE || value === '' ? null : Number(value)
    setForm((f) => ({ ...f, event_id: eventId, ticket_id: null }))
  }

  function handleUnlimitedToggle(checked: boolean) {
    setForm((f) => ({ ...f, max_uses: checked ? null : 1 }))
  }

  /**
   * Um ingresso grátis não gera receita, então não comissiona ninguém: ele é
   * classificado. Trocar de tipo limpa sempre o ramo abandonado.
   */
  function handleDiscountTypeChange(value: IEventCouponCreatePayload['discount_type']) {
    setForm((f) => ({
      ...f,
      discount_type: value,
      discount_value: null,
      free_coupon_category: value === 'free_ticket' ? f.free_coupon_category : null,
      ...(value === 'free_ticket' ? clearedCommission() : {})
    }))
  }

  // Espelha NormalisesEventCouponType no backend: os dados de pagamento são do parceiro,
  // então saem junto com ele. Sem isso o formulário mandaria dados que o backend descarta
  // em silêncio, e a tela continuaria exibindo o que já não existe.
  function clearedCommission() {
    return {
      commission_type: null,
      teacher_id: null,
      commercial_consultant_id: null,
      partner_name: null,
      partner_bank_code: null,
      partner_bank_name: null,
      partner_bank_agency: null,
      partner_bank_account: null,
      partner_bank_holder_name: null,
      partner_pix_key: null,
      partner_pix_key_type: null,
      partner_phone: null,
      commission_rate: null,
      commission_rate_sold_out: null
    }
  }

  function handleCommissionToggle(checked: boolean) {
    setForm((f) => (checked ? { ...f, commission_type: 'teacher' } : { ...f, ...clearedCommission() }))
  }

  /**
   * Trocar o tipo zera a chave: um valor mascarado como CPF não quer dizer nada relido
   * como e-mail, e reaproveitá-lo só produziria uma chave inválida difícil de perceber.
   */
  function handlePixKeyTypeChange(value: PixKeyType | null) {
    setForm((f) => ({ ...f, partner_pix_key_type: value, partner_pix_key: null }))
  }

  function handleCommissionTypeChange(value: CouponCommissionType) {
    // Só o alvo do novo tipo sobrevive: um cupom nunca guarda o beneficiário anterior.
    setForm((f) => ({ ...f, ...clearedCommission(), commission_type: value, commission_rate: f.commission_rate }))
  }

  function validationError(): string | null {
    if (!form.code) return 'Informe o código do cupom'
    if (showDiscountValue && form.discount_value == null) return 'Informe o valor do desconto'
    if (form.discount_type === 'percentage' && (form.discount_value ?? 0) > MAX_PERCENTAGE_DISCOUNT) {
      return 'Um desconto de 100% deve ser cadastrado com o tipo "Ingresso grátis"'
    }
    if (isFreeTicket && !form.free_coupon_category) return 'Selecione a categoria do cupom gratuito'
    if (form.commission_type === 'teacher' && form.teacher_id === null) return 'Selecione um professor'
    if (form.commission_type === 'partner' && !form.partner_name?.trim()) return 'Informe o nome do parceiro'
    if (form.commission_type === 'commercial' && form.commercial_consultant_id === null) {
      return 'Selecione um consultor comercial'
    }
    if (isCommissioned && form.commission_rate == null) return 'Informe o percentual de comissão'
    if (form.partner_pix_key && !form.partner_pix_key_type) return 'Selecione o tipo da chave Pix'
    // O telefone é opcional, mas um telefone pela metade não serve para ninguém.
    if (form.partner_phone && form.partner_phone.replace(/\D/g, '').length < 10) {
      return 'Informe o telefone do parceiro com o DDD'
    }
    return null
  }

  async function handleSubmit() {
    const error = validationError()
    if (error) {
      toast.error(error)
      return
    }

    setSaving(true)
    try {
      const payload: IEventCouponCreatePayload = {
        ...form,
        ticket_id: isGlobal ? null : form.ticket_id,
        discount_value: showDiscountValue ? form.discount_value : null
      }

      if (mode === 'create') {
        await service.create(payload)
      } else if (coupon) {
        // event_id is immutable after creation.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { event_id, ...updatePayload } = payload
        await service.update(coupon.id, updatePayload)
      }
      toast.success(mode === 'create' ? 'Cupom criado com sucesso!' : 'Cupom atualizado com sucesso!')
      onSuccess()
      onClose()
    } catch (err: unknown) {
      // A descrição sai do erro da API; o título é fixo porque o `message` de uma validação é o
      // "Validation error" em inglês.
      toast.error('Erro ao salvar cupom', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    // scrollBehavior="inside" prende a rolagem ao corpo: com o bloco de comissionamento
    // aberto o formulário passa da altura da tela, e por fora dele o modal inteiro subia,
    // levando junto o cabeçalho e os botões de salvar.
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Novo Cupom de Desconto' : 'Editar Cupom de Desconto'}
      onSubmit={handleSubmit}
      confirmLabel={mode === 'create' ? 'Criar cupom' : 'Salvar alterações'}
      isLoading={saving}
    >
      <Flex direction="column" gap={4}>
        <FormField label="Evento">
          <SearchableSelect
            options={eventOptions}
            value={form.event_id !== null ? String(form.event_id) : GLOBAL_EVENT_VALUE}
            onChange={handleEventChange}
            placeholder="Selecione o evento"
            isDisabled={mode === 'edit'}
          />
          <FormHelperText>
            Deixe em &ldquo;Todos os eventos&rdquo; para um cupom global, válido em qualquer evento. Eventos já
            encerrados não aparecem na lista.
          </FormHelperText>
        </FormField>

        {!isGlobal && (
          <FormField label="Ingresso (opcional — deixe vazio para todos)">
            <FormSelect
              isDisabled={!form.event_id}
              value={form.ticket_id ?? ''}
              onChange={(value) => setForm((f) => ({ ...f, ticket_id: value ? Number(value) : null }))}
              options={tickets.map((t) => ({ value: t.id, label: t.title }))}
              placeholder="Todos os ingressos"
            />
          </FormField>
        )}

        <FormField label="Código do cupom" isRequired>
          <Flex gap={2}>
            <Input
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="EX: ABRA2026"
              maxLength={50}
            />
            <Button onClick={generateCode} colorScheme="gray" flexShrink={0}>
              Gerar
            </Button>
          </Flex>
        </FormField>

        <FormField label="Tipo de desconto" isRequired>
          <FormSelect
            value={form.discount_type}
            onChange={(value) => handleDiscountTypeChange(value as IEventCouponCreatePayload['discount_type'])}
            options={discountTypeOptions.map((o) => ({ value: o.value, label: o.label }))}
            hideEmptyOption
          />
        </FormField>

        {showDiscountValue && (
          <FormControl>
            <FormLabel>
              Valor do desconto *{' '}
              <Text variant="caption" as="span">
                {form.discount_type === 'percentage' ? '(%)' : '(R$)'}
              </Text>
            </FormLabel>
            <NumberInput
              value={form.discount_value ?? ''}
              onChange={(_, val) => setForm((f) => ({ ...f, discount_value: isNaN(val) ? null : val }))}
              min={0.01}
              max={form.discount_type === 'percentage' ? MAX_PERCENTAGE_DISCOUNT : undefined}
            >
              <NumberInputField placeholder={form.discount_type === 'percentage' ? '10' : '50.00'} />
            </NumberInput>
            {form.discount_type === 'percentage' && (
              <FormHelperText>Para 100% de desconto, use o tipo &ldquo;Ingresso grátis&rdquo;.</FormHelperText>
            )}
          </FormControl>
        )}

        <Divider />

        {isFreeTicket ? (
          <FormField label="Categoria do cupom gratuito" isRequired>
            <FormSelect
              value={form.free_coupon_category ?? ''}
              onChange={(value) =>
                setForm((f) => ({ ...f, free_coupon_category: (value || null) as FreeCouponCategory }))
              }
              options={freeCouponCategoryOptions.map((o) => ({ value: o.value, label: o.label }))}
              placeholder="Selecione a categoria"
            />
            <FormHelperText>Aparece no gráfico de ingressos gratuitos do evento.</FormHelperText>
          </FormField>
        ) : (
          <>
            <FormField label="Cupom comissionado" display="flex" alignItems="center" gap={3}>
              <Switch isChecked={isCommissioned} onChange={(e) => handleCommissionToggle(e.target.checked)} />
            </FormField>

            {isCommissioned && (
              <>
                <FormField label="Tipo de comissão" isRequired>
                  <FormSelect
                    value={form.commission_type ?? ''}
                    onChange={(value) => handleCommissionTypeChange(value as CouponCommissionType)}
                    options={commissionTypeOptions.map((o) => ({ value: o.value, label: o.label }))}
                    hideEmptyOption
                  />
                </FormField>

                {form.commission_type === 'teacher' && (
                  <FormField label="Professor" isRequired>
                    <SearchableSelect
                      options={teacherOptions}
                      value={form.teacher_id !== null ? String(form.teacher_id) : ''}
                      onChange={(value) => setForm((f) => ({ ...f, teacher_id: value ? Number(value) : null }))}
                      placeholder="Selecione o professor"
                      isClearable
                    />
                  </FormField>
                )}

                {form.commission_type === 'partner' && (
                  <>
                    <FormField label="Parceiro" isRequired>
                      <Input
                        value={form.partner_name ?? ''}
                        onChange={(e) => setForm((f) => ({ ...f, partner_name: e.target.value || null }))}
                        placeholder="EX: Livraria Cultura"
                        maxLength={120}
                      />
                      <FormHelperText>
                        Cupons com o mesmo nome de parceiro somam na mesma linha da comissão.
                      </FormHelperText>
                    </FormField>

                    <Text fontSize="sm" fontWeight="600" color="gray.600">
                      Dados para pagamento (opcional)
                    </Text>

                    <FormField label="Banco">
                      <BankSelect
                        code={form.partner_bank_code ?? null}
                        name={form.partner_bank_name ?? null}
                        onChange={(code, name) =>
                          setForm((f) => ({ ...f, partner_bank_code: code, partner_bank_name: name }))
                        }
                      />
                    </FormField>

                    <Flex gap={4}>
                      <FormField label="Agência">
                        <Input
                          value={form.partner_bank_agency ?? ''}
                          onChange={(e) => setForm((f) => ({ ...f, partner_bank_agency: e.target.value || null }))}
                          placeholder="EX: 0001"
                          maxLength={20}
                        />
                      </FormField>
                      <FormField label="Conta corrente">
                        <Input
                          value={form.partner_bank_account ?? ''}
                          onChange={(e) => setForm((f) => ({ ...f, partner_bank_account: e.target.value || null }))}
                          placeholder="EX: 12345-6"
                          maxLength={20}
                        />
                      </FormField>
                    </Flex>

                    <FormField label="Nome do titular">
                      <Input
                        value={form.partner_bank_holder_name ?? ''}
                        onChange={(e) => setForm((f) => ({ ...f, partner_bank_holder_name: e.target.value || null }))}
                        placeholder="Quem recebe o pagamento"
                        maxLength={255}
                      />
                    </FormField>

                    <FormField label="Tipo da chave Pix">
                      <FormSelect
                        value={form.partner_pix_key_type ?? ''}
                        onChange={(value) => handlePixKeyTypeChange((value || null) as PixKeyType | null)}
                        options={pixKeyTypeOptions.map((o) => ({ value: o.value, label: o.label }))}
                        placeholder="Selecione o tipo"
                      />
                    </FormField>

                    <FormField label="Chave Pix">
                      <MaskedInput
                        mask={pixKeyMask}
                        value={form.partner_pix_key}
                        onChange={(partner_pix_key) =>
                          setForm((f) => ({ ...f, partner_pix_key: partner_pix_key || null }))
                        }
                        // Sem o tipo não há máscara nem validação possível, então o campo vazio
                        // espera a escolha. Uma chave já gravada continua legível e apagável:
                        // travá-la deixaria o dado preso na tela, sem como copiar nem corrigir.
                        isDisabled={!form.partner_pix_key_type && !form.partner_pix_key}
                        {...(pixKeyMask ? {} : { placeholder: 'Selecione o tipo da chave' })}
                      />
                    </FormField>

                    <FormField label="Telefone de contato">
                      <MaskedInput
                        mask="phone"
                        value={form.partner_phone}
                        onChange={(partner_phone) => setForm((f) => ({ ...f, partner_phone: partner_phone || null }))}
                      />
                    </FormField>
                  </>
                )}

                {form.commission_type === 'commercial' && (
                  <FormField label="Consultor comercial" isRequired>
                    <SearchableSelect
                      options={consultantOptions}
                      value={form.commercial_consultant_id !== null ? String(form.commercial_consultant_id) : ''}
                      onChange={(value) =>
                        setForm((f) => ({ ...f, commercial_consultant_id: value ? Number(value) : null }))
                      }
                      placeholder="Selecione o consultor"
                      isClearable
                    />
                  </FormField>
                )}

                <FormField label="Comissão (%)" isRequired>
                  <NumberInput
                    value={form.commission_rate ?? ''}
                    onChange={(_, val) => setForm((f) => ({ ...f, commission_rate: isNaN(val) ? null : val }))}
                    min={0.01}
                    max={100}
                  >
                    <NumberInputField placeholder="10" />
                  </NumberInput>
                </FormField>

                <FormField label="Comissão se o evento esgotar (%)">
                  <NumberInput
                    value={form.commission_rate_sold_out ?? ''}
                    onChange={(_, val) => setForm((f) => ({ ...f, commission_rate_sold_out: isNaN(val) ? null : val }))}
                    min={0.01}
                    max={100}
                  >
                    <NumberInputField placeholder="Opcional" />
                  </NumberInput>
                  <FormHelperText>Em branco, um evento esgotado paga a mesma comissão acima.</FormHelperText>
                </FormField>
              </>
            )}
          </>
        )}

        <Divider />

        <FormField label="Uso ilimitado" display="flex" alignItems="center" gap={3}>
          <Switch isChecked={isUnlimited} onChange={(e) => handleUnlimitedToggle(e.target.checked)} />
        </FormField>

        {!isUnlimited && (
          <FormField label="Máximo de usos" isRequired>
            <NumberInput
              value={form.max_uses ?? 1}
              onChange={(_, val) => setForm((f) => ({ ...f, max_uses: isNaN(val) ? 1 : val }))}
              min={1}
            >
              <NumberInputField />
            </NumberInput>
          </FormField>
        )}

        <FormControl>
          <Tooltip label="Deixe em branco para sem validade">
            <FormLabel>Data de validade (opcional)</FormLabel>
          </Tooltip>
          <Input
            type="date"
            value={form.expires_at ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value || null }))}
          />
        </FormControl>

        <FormField label="Ativo" display="flex" alignItems="center" gap={3}>
          <Switch
            isChecked={form.is_active ?? true}
            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
          />
        </FormField>
      </Flex>
    </FormModal>
  )
}
